<!-- src/components/modals/InstallGuideModal.vue -->
<template>
  <div v-if="isOpen" class="fixed inset-0 z-[100] overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 transition-opacity bg-slate-900 bg-opacity-90 backdrop-blur-md" @click="$emit('close')"></div>

      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
      <div class="inline-block overflow-hidden text-left align-bottom transition-all transform bg-white rounded-[2.5rem] shadow-2xl sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full border border-white/20">
        
        <!-- Header -->
        <div class="bg-gradient-to-r from-slate-50 to-white px-8 py-6 border-b border-gray-100 flex justify-between items-center">
          <div class="flex items-center gap-5">
            <div class="bg-brand-primary p-3 rounded-2xl shadow-xl rotate-3">
              <img src="/images/sentinel-safe-logo.png" alt="Logo" class="h-7 w-auto brightness-0 invert">
            </div>
            <div>
              <h3 class="text-2xl font-black text-slate-800 tracking-tight leading-none">Sentinel Safe</h3>
              <p class="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">Universal Installation Guide</p>
            </div>
          </div>
          <button @click="$emit('close')" class="text-slate-300 hover:text-slate-600 transition-all p-2 hover:bg-slate-100 rounded-full">
            <svg class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <!-- Platform Tabs -->
        <div class="flex bg-white sticky top-0 z-10 border-b border-gray-100 px-4">
          <button @click="activeTab = 'android'" :class="['tab-btn', activeTab === 'android' ? 'active-tab text-brand-accent' : 'inactive-tab']">
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.523 15.3414C16.897 15.3414 16.388 14.8324 16.388 14.2064C16.388 13.5804 16.897 13.0714 17.523 13.0714C18.149 13.0714 18.658 13.5804 18.658 14.2064C18.658 14.8324 18.149 15.3414 17.523 15.3414ZM6.477 15.3414C5.851 15.3414 5.342 14.8324 5.342 14.2064C5.342 13.5804 5.851 13.0714 6.477 13.0714C7.103 13.0714 7.612 13.5804 7.612 14.2064C7.612 14.8324 7.103 15.3414 6.477 15.3414ZM17.905 10.5184L19.86 7.13242C20.024 6.84842 19.927 6.48642 19.643 6.32242C19.359 6.15842 18.997 6.25542 18.833 6.53942L16.845 9.98342C15.426 9.33642 13.791 8.97142 12 8.97142C10.209 8.97142 8.574 9.33642 7.155 9.98342L5.167 6.53942C5.003 6.25542 4.641 6.15842 4.357 6.32242C4.073 6.48642 3.976 6.84842 4.14 7.13242L6.095 10.5184C3.013 12.1834 0.909 15.3414 0.612 19.0284H23.388C23.091 15.3414 20.987 12.1834 17.905 10.5184Z"/></svg>
            Android
          </button>
          <button @click="activeTab = 'ios'" :class="['tab-btn', activeTab === 'ios' ? 'active-tab text-brand-primary' : 'inactive-tab']">
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.1 2.48-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91 1.34.03 2.5.52 3.26 1.44-3.1 1.27-2.6 5.59.45 6.64-.7 1.75-1.58 3.51-2.92 5.58M15.98 5.45c.67-.82 1.13-1.96.99-3.1-1.01.04-2.22.65-2.93 1.48-.64.73-1.2 1.88-1.04 2.98 1.12.09 2.24-.55 2.98-1.36Z"/></svg>
            iPhone / iPad
          </button>
        </div>

        <!-- Content Area -->
        <div class="p-8 md:p-12 bg-white max-h-[70vh] overflow-y-auto custom-scrollbar">
          
          <!-- Android Section -->
          <div v-if="activeTab === 'android'" class="animate-fadeIn space-y-24 pb-12">
            <div v-for="step in 6" :key="'android-'+step" class="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div :class="['space-y-6', step % 2 === 0 ? 'md:order-2' : '']">
                <div class="flex items-start gap-6">
                  <span class="step-number bg-brand-accent/10 text-brand-accent border-brand-accent/20">{{ step }}</span>
                  <div>
                    <h4 class="step-title">{{ androidInstructions[step-1].title }}</h4>
                    <p class="step-desc" v-html="androidInstructions[step-1].desc"></p>
                    <div v-if="step === 1" class="step-url-container border-brand-accent/20 text-brand-accent">
                       <span class="opacity-50 mr-1">https://</span>sentinel-safe.app
                    </div>
                  </div>
                </div>
              </div>
              <div :class="['screenshot-wrapper', step % 2 === 0 ? 'md:order-1' : '']">
                <img :src="'/images/install/android-' + step + '.png?v=6'" :alt="'Android step ' + step" class="screenshot">
                <div class="screenshot-label">Android View</div>
              </div>
            </div>

            <div class="final-success-banner bg-brand-accent">
               <div class="flex items-center justify-between gap-6 text-slate-900">
                 <div>
                   <h5 class="text-xl font-black">All Done!</h5>
                   <p class="text-sm font-bold opacity-80">Your digital safety portal is now ready on your device.</p>
                 </div>
                 <div class="bg-white/30 p-4 rounded-full">
                    <svg class="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" /></svg>
                 </div>
               </div>
            </div>
          </div>

          <!-- iOS Section -->
          <div v-if="activeTab === 'ios'" class="animate-fadeIn space-y-24 pb-12">
            <div class="ios-alert bg-brand-primary/5 border-brand-primary/10">
              <div class="bg-brand-primary p-2 rounded-lg">
                 <svg class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              </div>
              <p class="text-sm font-black text-brand-primary">Apple requires the Safari browser for Home Screen installation.</p>
            </div>

            <div v-for="step in 6" :key="'ios-'+step" class="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div :class="['space-y-6', step % 2 === 0 ? 'md:order-2' : '']">
                <div class="flex items-start gap-6">
                  <span class="step-number bg-brand-primary/10 text-brand-primary border-brand-primary/20">{{ step }}</span>
                  <div>
                    <h4 class="step-title">{{ iosInstructions[step-1].title }}</h4>
                    <p class="step-desc" v-html="iosInstructions[step-1].desc"></p>
                    <div v-if="step === 1" class="step-url-container border-brand-primary/20 text-brand-primary">
                       <span class="opacity-50 mr-1">https://</span>sentinel-safe.app
                    </div>
                  </div>
                </div>
              </div>
              <div :class="['screenshot-wrapper', step % 2 === 0 ? 'md:order-1' : '']">
                <!-- Bulletproof Pathing -->
                <img :src="'/images/install/ios-' + step + '.png?v=6'" :alt="'iOS step ' + step" class="screenshot">
                <div class="screenshot-label">iPhone View</div>
              </div>
            </div>

            <div class="final-success-banner bg-brand-primary">
               <div class="flex items-center justify-between gap-6 text-white">
                 <div>
                   <h5 class="text-xl font-black">Ready for Duty</h5>
                   <p class="text-sm font-bold opacity-80">Installation complete. Open from your home screen to begin.</p>
                 </div>
                 <div class="bg-white/20 p-4 rounded-full">
                    <svg class="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" /></svg>
                 </div>
               </div>
            </div>
          </div>
        </div>

        <div class="bg-slate-50 px-8 py-8 border-t border-gray-100 flex justify-center">
          <button @click="$emit('close')" class="close-guide-btn">Got it, thanks!</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';
const props = defineProps({ isOpen: Boolean, initialTab: { type: String, default: 'android' } });
defineEmits(['close']);
const activeTab = ref(props.initialTab);
watch(() => props.initialTab, (newVal) => { activeTab.value = newVal; });

const androidInstructions = [
  { title: 'Visit the Portal', desc: 'Open <span class="font-black text-slate-800">Google Chrome</span> and type the web address into the browser bar.' },
  { title: 'Access Menu', desc: 'Once the page loads, tap the <span class="font-black text-slate-800">Three Dots (⋮)</span> in the top right corner of Chrome.' },
  { title: 'Select Install', desc: 'Scroll down the menu and tap <span class="font-black text-brand-accent">"Add to Home screen"</span> or <span class="font-black text-brand-accent">"Install app"</span>.' },
  { title: 'Add to Screen', desc: 'A prompt will appear showing the app icon. Tap the <span class="font-black text-slate-800">"Add"</span> button to proceed.' },
  { title: 'Confirm Action', desc: 'A final system dialog will ask for permission. Tap <span class="font-black text-brand-accent">"Install"</span> to finalize the setup.' },
  { title: 'Launch App', desc: 'The <span class="font-black text-slate-800">Sentinel Safe</span> icon is now on your Home Screen. Tap it any time to begin.' }
];

const iosInstructions = [
  { title: 'Open Safari', desc: 'Launch the <span class="font-black text-slate-800">Safari</span> browser and navigate to our portal address.' },
  { title: 'Visit Site', desc: 'Allow the landing page to load completely before proceeding to the next step.' },
  { title: 'Tap Share', desc: 'Tap the <span class="font-black text-slate-800">Share Icon</span> (square with upward arrow) at the bottom middle of the screen.' },
  { title: 'Add to Home', desc: 'Scroll down the share menu and select the <span class="font-black text-brand-primary">"Add to Home Screen"</span> option.' },
  { title: 'Confirm Name', desc: 'Ensure "Sentinel Safe" is the name displayed and tap <span class="font-black text-slate-800">Add</span> in the top right corner.' },
  { title: 'Start Using', desc: 'The app icon is now visible on your iPhone home screen. Tap it to start working.' }
];
</script>

<style scoped>
.tab-btn { @apply flex-1 py-6 flex items-center justify-center gap-3 transition-all duration-300 font-black uppercase tracking-widest text-xs border-b-2 border-transparent; }
.active-tab { @apply border-b-4 border-current bg-slate-50/50; }
.inactive-tab { @apply text-slate-400 hover:text-slate-600 hover:bg-slate-50/30; }
.step-number { @apply flex-shrink-0 w-12 h-12 rounded-[1.2rem] flex items-center justify-center font-black text-lg shadow-sm border-2; }
.step-title { @apply font-black text-slate-800 text-2xl tracking-tight mb-2 leading-tight; }
.step-desc { @apply text-slate-500 text-base leading-relaxed font-medium max-w-sm; }
.step-url-container { @apply text-sm font-mono bg-slate-50 border px-5 py-3 rounded-2xl inline-block shadow-inner mt-4 font-bold; }
.screenshot-wrapper { @apply relative p-3 bg-slate-50 rounded-[3.5rem] border border-slate-100 shadow-xl max-w-[280px] mx-auto transition-transform hover:scale-[1.02] duration-500; }
.screenshot { @apply rounded-[3rem] shadow-2xl border-4 border-white w-full h-auto aspect-[9/19] object-cover bg-slate-200; }
.screenshot-label { @apply absolute -bottom-4 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[10px] font-black px-5 py-2 rounded-full shadow-2xl uppercase tracking-[0.2em] whitespace-nowrap; }
.ios-alert { @apply flex items-center gap-4 p-5 rounded-3xl border mb-12; }
.final-success-banner { @apply p-10 rounded-[3rem] shadow-2xl mt-12 transform -rotate-1; }
.close-guide-btn { @apply bg-brand-primary text-white px-16 py-5 rounded-[2rem] font-black hover:bg-brand-accent hover:text-slate-900 transition-all duration-300 shadow-2xl hover:shadow-brand-primary/20 text-sm uppercase tracking-widest active:scale-95; }
.animate-fadeIn { animation: fadeIn 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
.custom-scrollbar::-webkit-scrollbar { width: 8px; }
.custom-scrollbar::-webkit-scrollbar-track { @apply bg-transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { @apply bg-slate-200 rounded-full border-4 border-white hover:bg-slate-300; }
@media (max-width: 768px) { .step-desc { @apply max-w-none; } .screenshot-wrapper { @apply mt-8; } }
</style>